import { useMemo, useState } from 'react';
import customOrdersService from '../../services/customorders.service';
import { useCustomOrders } from '../../hooks';
import Spinner from '../common/Spinner';
import SearchBar from '../common/SearchBar';
import Modal from '../common/Modal';
import { formatDateInSpanish } from '../../helpers/general';
import { STORE_OPTIONS, getStoreLabel } from '../../constants/stores';

const STATUS_SEQUENCE = ['created', 'ready', 'delivered'];
const STATUS_LABELS = {
    created: 'Creado',
    ready: 'Listo',
    delivered: 'Entregado'
};

function normalizeStatus(status) {
    if (!status || status === 'pending') return 'created';
    if (STATUS_SEQUENCE.includes(status)) return status;
    return 'created';
}

function getNextStatusValue(status) {
    const normalized = normalizeStatus(status);
    const currentIndex = STATUS_SEQUENCE.indexOf(normalized);
    if (currentIndex === -1) return 'created';
    return STATUS_SEQUENCE[(currentIndex + 1) % STATUS_SEQUENCE.length];
}

function getStatusLabelText(status) {
    const normalized = normalizeStatus(status);
    return STATUS_LABELS[normalized] || normalized;
}

function getNextStatusActionText(status) {
    const next = getNextStatusValue(status);
    if (next === 'delivered') return 'Marcar como entregado';
    if (next === 'ready') return 'Marcar como listo';
    return 'Reiniciar a creado';
}

export default function CustomOrdersList() {
    const { orders, loading, activeStoreId } = useCustomOrders();
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('upcoming');
    const [editingOrder, setEditingOrder] = useState(null);
    const [editForm, setEditForm] = useState(null);
    const [saving, setSaving] = useState(false);

    const todayStr = new Date().toISOString().split('T')[0];

    const baseFilters = useMemo(() => {
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        const monthAgoStr = monthAgo.toISOString().split('T')[0];
        return orders.filter((order) => {
            if (activeStoreId && (order.storeId || '') !== activeStoreId) {
                return false;
            }
            if (order.pickupDate < monthAgoStr) return false;
            if (dateFilter === 'today' && order.pickupDate !== todayStr) return false;
            if (dateFilter === 'upcoming' && order.pickupDate < todayStr) return false;
            return true;
        });
    }, [orders, activeStoreId, dateFilter, todayStr]);

    const filteredOrders = useMemo(() => {
        return baseFilters.filter((order) => {
            const normalizedStatus = normalizeStatus(order.status);
            if (statusFilter !== 'all' && normalizedStatus !== statusFilter) {
                return false;
            }
            if (!searchQuery) return true;
            const haystack = `${order.customerName} ${order.customerPhone} ${order.department} ${getStoreLabel(order.storeId)}`.toLowerCase();
            return haystack.includes(searchQuery.toLowerCase());
        });
    }, [baseFilters, statusFilter, searchQuery]);

    const summaryCounts = useMemo(() => {
        const total = baseFilters.length;
        const ready = baseFilters.filter((order) => normalizeStatus(order.status) === 'ready').length;
        const delivered = baseFilters.filter((order) => normalizeStatus(order.status) === 'delivered').length;
        return { total, ready, delivered };
    }, [baseFilters]);

    const timeSlots = useMemo(() => {
        const slots = [];
        const startMinutes = 8 * 60;
        const endMinutes = 21 * 60;
        for (let minutes = startMinutes; minutes <= endMinutes; minutes += 30) {
            const hour24 = Math.floor(minutes / 60);
            const mins = (minutes % 60).toString().padStart(2, '0');
            const period = hour24 >= 12 ? 'PM' : 'AM';
            const hour12Raw = hour24 % 12 === 0 ? 12 : hour24 % 12;
            const display = `${hour12Raw}:${mins} ${period}`;
            const value = `${hour24.toString().padStart(2, '0')}:${mins}`;
            slots.push({ value, display });
        }
        return slots;
    }, []);

    const createdCount = summaryCounts.total;
    const readyCount = summaryCounts.ready;
    const deliveredCount = summaryCounts.delivered;

    async function handleStatusAdvance(order) {
        const nextStatus = getNextStatusValue(order.status);
        try {
            await customOrdersService.updateOrderStatus(order.id, nextStatus);
        } catch (error) {
            console.error('Error updating custom order status', error);
            alert('No se pudo actualizar el estado.');
        }
    }

    async function handleDelete(order) {
        const confirmDelete = window.confirm('Borrar esta orden? Esta accion no se puede deshacer.');
        if (!confirmDelete) return;
        try {
            await customOrdersService.deleteOrder(order.id);
            if (editingOrder && editingOrder.id === order.id) {
                closeEditModal();
            }
        } catch (error) {
            console.error('Error deleting order', error);
            alert('No se pudo borrar la orden.');
        }
    }

    function formatPickupTime(order) {
        let date;
        if (order.pickupDateTimeUtc) {
            date = new Date(order.pickupDateTimeUtc);
        } else if (order.pickupDate && order.pickupTime) {
            date = new Date(`${order.pickupDate}T${order.pickupTime}:00`);
        } else if (order.pickupTime) {
            const [hour, minute] = order.pickupTime.split(':');
            date = new Date();
            date.setHours(Number(hour), Number(minute), 0, 0);
        } else {
            return '';
        }
        const hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const period = hours >= 12 ? 'PM' : 'AM';
        const hour12 = hours % 12 === 0 ? 12 : hours % 12;
        return `${hour12}:${minutes} ${period}`;
    }

    function openEditModal(order) {
        setEditingOrder(order);
        setEditForm({
            customerName: order.customerName || '',
            customerPhone: order.customerPhone || '',
            department: order.department || '',
            storeId: order.storeId || '',
            pickupDate: order.pickupDate || '',
            pickupTime: order.pickupTime || '',
            items: order.items?.map((item) => ({ ...item })) || [{ name: '', quantity: 1, price: '' }],
            paid: Boolean(order.paid)
        });
    }

    function closeEditModal() {
        setEditingOrder(null);
        setEditForm(null);
        setSaving(false);
    }

    function handleFormFieldChange(field, value) {
        if (field === 'paid') {
            setEditForm((prev) => ({ ...prev, [field]: value === true || value === 'yes' }));
        } else {
            setEditForm((prev) => ({ ...prev, [field]: value }));
        }
    }

    function handleFormItemChange(index, field, value) {
        setEditForm((prev) => {
            const updatedItems = prev.items.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            );
            return { ...prev, items: updatedItems };
        });
    }

    function addFormItem() {
        setEditForm((prev) => ({ ...prev, items: [...prev.items, { name: '', quantity: 1, price: '' }] }));
    }

    function removeFormItem(index) {
        setEditForm((prev) => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    }

    async function saveEdit() {
        if (!editingOrder || !editForm) return;
        if (
            !editForm.customerName ||
            !editForm.customerPhone ||
            !editForm.department ||
            !editForm.storeId ||
            !editForm.pickupDate ||
            !editForm.pickupTime
        ) {
            alert('Por favor llena los campos requeridos.');
            return;
        }
        const sanitizedItems = editForm.items
            .filter((item) => item.name)
            .map((item) => ({
                name: item.name,
                quantity: Number(item.quantity) || 0,
                price:
                    item.price === '' || item.price === null || item.price === undefined
                        ? null
                        : Number(item.price)
            }));
        if (sanitizedItems.length === 0) {
            alert('Se requiere al menos un articulo.');
            return;
        }

        setSaving(true);
        try {
            const pickupUtc = new Date(`${editForm.pickupDate}T${editForm.pickupTime}:00`).toISOString();
            await customOrdersService.updateOrder(editingOrder.id, {
                customerName: editForm.customerName,
                customerPhone: editForm.customerPhone,
                department: editForm.department,
                store_id: editForm.storeId,
                pickupDate: editForm.pickupDate,
                pickupTime: editForm.pickupTime,
                pickupDateTimeUtc: pickupUtc,
                paid: !!editForm.paid,
                items: sanitizedItems
            });
            closeEditModal();
        } catch (error) {
            console.error('Error updating order', error);
            alert('No se pudieron guardar los cambios.');
            setSaving(false);
        }
    }

    return (
        <div className="custom-orders-list">
            <div className="custom-form-header">
                <div>
                    <h2>Tablero de Ordenes</h2>
                </div>
            </div>

            <div className="custom-summary">
                <div className="custom-summary-card">
                    <span>Total</span>
                    <strong>{createdCount}</strong>
                </div>
                <div className="custom-summary-card" style={{ background: '#e8f5ff' }}>
                    <span>Listos</span>
                    <strong>{readyCount}</strong>
                </div>
                <div className="custom-summary-card" style={{ background: '#e8ffe8' }}>
                    <span>Entregados</span>
                    <strong>{deliveredCount}</strong>
                </div>
            </div>

            <div className="custom-orders-filters">
                <SearchBar onSearch={setSearchQuery} />
                <div className="custom-orders-filter-row">
                    <div>
                        <strong>Estado:</strong>
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="all">Todos</option>
                            <option value="created">Creados</option>
                            <option value="ready">Listos</option>
                            <option value="delivered">Entregados</option>
                        </select>
                    </div>
                    <div>
                        <strong>Fecha:</strong>
                        <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                            <option value="upcoming">Proximos (Hoy+)</option>
                            <option value="today">Hoy</option>
                            <option value="all">Todos</option>
                        </select>
                    </div>
                </div>
            </div>

            {loading ? (
                <Spinner />
            ) : filteredOrders.length === 0 ? (
                <p>No se encontraron ordenes.</p>
            ) : (
                <div className="custom-order-grid-wrapper">
                    <div className="custom-order-grid">
                        <div className="grid-row grid-header">
                            <div>Estado</div>
                            <div>Cliente</div>
                            <div>Telefono</div>
                            <div>Depto.</div>
                            <div>Tienda</div>
                            <div>Fecha</div>
                            <div>Hora</div>
                            <div>Articulos</div>
                            <div>Pagado</div>
                        </div>
                        {filteredOrders.map((order) => (
                            <OrderGridRow
                                key={order.id}
                                order={order}
                                formatDateInSpanish={formatDateInSpanish}
                                formatPickupTime={formatPickupTime}
                                onEdit={openEditModal}
                                onAdvance={handleStatusAdvance}
                                storeLabel={getStoreLabel(order.storeId)}
                            />
                        ))}
                    </div>
                </div>
            )}

            {editingOrder && editForm && (
                <Modal onClose={closeEditModal}>
                    <div className="custom-edit-modal">
                        <h3>Editar Orden</h3>
                        <div className="custom-edit-grid">
                            <label>
                                Nombre
                                <input
                                    type="text"
                                    value={editForm.customerName}
                                    onChange={(e) => handleFormFieldChange('customerName', e.target.value)}
                                />
                            </label>
                            <label>
                                Telefono
                                <input
                                    type="text"
                                    value={editForm.customerPhone}
                                    onChange={(e) => handleFormFieldChange('customerPhone', e.target.value)}
                                />
                            </label>
                            <label>
                                Departamento
                                <input
                                    type="text"
                                    value={editForm.department}
                                    onChange={(e) => handleFormFieldChange('department', e.target.value)}
                                />
                            </label>
                            <label>
                                Tienda
                                <select
                                    value={editForm.storeId}
                                    onChange={(e) => handleFormFieldChange('storeId', e.target.value)}
                                >
                                    <option value="">Seleccionar...</option>
                                    {STORE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                Fecha
                                <input
                                    type="date"
                                    value={editForm.pickupDate}
                                    onChange={(e) => handleFormFieldChange('pickupDate', e.target.value)}
                                />
                            </label>
                            <label>
                                Hora
                                <select
                                    value={editForm.pickupTime}
                                    onChange={(e) => handleFormFieldChange('pickupTime', e.target.value)}
                                >
                                    <option value="">Seleccionar...</option>
                                    {timeSlots.map((slot) => (
                                        <option value={slot.value} key={slot.value}>
                                            {slot.display}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                Pagado?
                                <select
                                    value={editForm.paid ? 'yes' : 'no'}
                                    onChange={(e) => handleFormFieldChange('paid', e.target.value)}
                                >
                                    <option value="no">No</option>
                                    <option value="yes">Si</option>
                                </select>
                            </label>
                        </div>

                        <div className="custom-edit-items">
                            <div className="custom-edit-items-header">
                                <span>Articulos</span>
                                <button type="button" className="secondaryButton" onClick={addFormItem}>
                                    + Articulo
                                </button>
                            </div>
                            {editForm.items.map((item, index) => (
                                <div className="custom-edit-item-row" key={`edit-item-${index}`}>
                                    <input
                                        type="number"
                                        min="1"
                                        value={item.quantity}
                                        onChange={(e) => handleFormItemChange(index, 'quantity', e.target.value)}
                                    />
                                    <input
                                        type="text"
                                        value={item.name}
                                        onChange={(e) => handleFormItemChange(index, 'name', e.target.value)}
                                    />
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={item.price ?? ''}
                                        onChange={(e) => handleFormItemChange(index, 'price', e.target.value)}
                                    />
                                    {editForm.items.length > 1 && (
                                        <button
                                            type="button"
                                            className="lightButton"
                                            onClick={() => removeFormItem(index)}
                                        >
                                            Eliminar
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="custom-edit-actions">
                            <button type="button" className="lightButton" onClick={closeEditModal}>
                                Cancelar
                            </button>
                            <div className="custom-edit-actions-right">
                                <button
                                    type="button"
                                    className="dangerButton"
                                    onClick={() => handleDelete(editingOrder)}
                                    title="Borrar"
                                >
                                    🗑️
                                </button>
                                <button
                                    type="button"
                                    className="primaryButton"
                                    onClick={saveEdit}
                                    disabled={saving}
                                >
                                    {saving ? 'Guardando...' : 'Guardar'}
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

function OrderGridRow({ order, formatDateInSpanish, formatPickupTime, onEdit, onAdvance, storeLabel }) {
    const normalizedStatus = normalizeStatus(order.status);
    const nextStatus = getNextStatusValue(normalizedStatus);

    return (
        <div
            className="grid-row clickable-order-row"
            onClick={() => onEdit(order)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onEdit(order);
                }
            }}
        >
            <div className="grid-status-cell">
                <span className={`custom-order-status ${normalizedStatus}`}>
                    {getStatusLabelText(normalizedStatus)}
                </span>
                <span className="status-next-hint">
                    Siguiente: {getStatusLabelText(nextStatus)}
                </span>
                <button
                    type="button"
                    className="status-advance-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onAdvance(order);
                    }}
                    title={getNextStatusActionText(normalizedStatus)}
                    aria-label={`Avanzar a estado ${getStatusLabelText(nextStatus)}`}
                >
                    Avanzar
                </button>
            </div>
            <div>{order.customerName}</div>
            <div>{order.customerPhone}</div>
            <div>{order.department}</div>
            <div>{storeLabel}</div>
            <div>{formatDateInSpanish(order.pickupDate)}</div>
            <div>{formatPickupTime(order)}</div>
            <div className="grid-items-cell">
                {order.items.map((item, index) => (
                    <span key={`${order.id}-item-${index}`}>
                        {item.quantity} x {item.name}
                    </span>
                ))}
            </div>
            <div>
                <span className={`paid-pill ${order.paid ? 'yes' : 'no'}`}>
                    {order.paid ? 'Si' : 'No'}
                </span>
            </div>
        </div>
    );
}
